import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, Info } from "lucide-react";
import { apiRequest, queryClient, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export type ConversationSource = "partner" | "inquiry";

const COMPLIANCE_NOTICE =
  "Conversations on PadPulse are monitored by PadPulse for quality and compliance.";

type RawPartnerMessage = {
  id: number;
  senderType: string;
  senderName: string | null;
  content: string;
  createdAt: string;
};

type RawInquiryMessage = {
  id: number;
  senderId: number;
  content: string;
  createdAt: string;
};

type NormalizedMessage = {
  id: number;
  mine: boolean;
  senderName: string | null;
  content: string;
  createdAt: string;
};

function endpointBase(source: ConversationSource): string {
  return source === "partner" ? "/api/app/partner-threads" : "/api/app/sell/inquiries";
}

function listKey(source: ConversationSource): string {
  return source === "partner" ? "/api/app/partner-threads" : "/api/app/sell/inquiries";
}

/**
 * Single shared chat UI for every non-vendor, non-member host conversation.
 * Used by the Connections hub, the partner conversations page, and the Buy/Sell
 * "My Inquiries" view so there is exactly one conversation experience to maintain.
 */
export function ConversationChat({
  source,
  conversationId,
  currentUserId,
  counterpartyName,
  disabled,
  disabledPlaceholder,
  heightClass = "h-[480px]",
}: {
  source: ConversationSource;
  conversationId: number;
  currentUserId?: number | null;
  counterpartyName: string;
  disabled?: boolean;
  disabledPlaceholder?: string;
  heightClass?: string;
}) {
  const { toast } = useToast();
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const base = endpointBase(source);

  const { data: rawMessages, isLoading } = useQuery<RawPartnerMessage[] | RawInquiryMessage[]>({
    queryKey: [base, conversationId, "messages"],
    refetchInterval: 10000,
  });

  const messages: NormalizedMessage[] = (rawMessages || []).map((m) => {
    if (source === "partner") {
      const pm = m as RawPartnerMessage;
      return {
        id: pm.id,
        mine: pm.senderType === "user",
        senderName: pm.senderName,
        content: pm.content,
        createdAt: pm.createdAt,
      };
    }
    const im = m as RawInquiryMessage;
    const mine = im.senderId === currentUserId;
    return {
      id: im.id,
      mine,
      senderName: mine ? "You" : counterpartyName,
      content: im.content,
      createdAt: im.createdAt,
    };
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  // Opening a conversation marks it read server-side (both sources), so refresh
  // the unread surfaces once messages have loaded.
  useEffect(() => {
    if (!isLoading) {
      queryClient.invalidateQueries({ queryKey: ["/api/app/connections"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/connections/unread-count"] });
      queryClient.invalidateQueries({ queryKey: [listKey(source)] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, conversationId]);

  const sendMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await apiRequest("POST", `${base}/${conversationId}/messages`, { content });
      return res.json();
    },
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey: [base, conversationId, "messages"] });
      queryClient.invalidateQueries({ queryKey: [listKey(source)] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/connections"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/connections/unread-count"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/notifications/count"] });
    },
    onError: (err: unknown) => {
      toast({ title: "Couldn't send message", description: parseErrorMessage(err, "Try again"), variant: "destructive" });
    },
  });

  function handleSend() {
    const trimmed = draft.trim();
    if (!trimmed || sendMutation.isPending || disabled) return;
    sendMutation.mutate(trimmed);
  }

  return (
    <div className={`border border-gray-200 rounded-xl bg-white flex flex-col ${heightClass}`} data-testid="conversation-chat">
      {source === "partner" && (
        <div
          className="flex items-start gap-2 bg-amber-50 border-b border-amber-100 px-4 py-2 text-xs text-amber-700"
          data-testid="notice-compliance"
        >
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          {COMPLIANCE_NOTICE}
        </div>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50">
        {isLoading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : messages.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No messages yet.</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                  m.mine ? "bg-blue-600 text-white" : "bg-white border border-gray-200 text-gray-900"
                }`}
                data-testid={`conversation-message-${m.id}`}
              >
                {!m.mine && m.senderName && (
                  <p className="text-[11px] font-medium mb-0.5 text-gray-500">{m.senderName}</p>
                )}
                <p className="whitespace-pre-wrap break-words">{m.content}</p>
                <p className={`text-[10px] mt-1 ${m.mine ? "text-blue-100" : "text-gray-400"}`}>
                  {new Date(m.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="p-3 border-t border-gray-100 flex gap-2">
        <Textarea
          value={draft}
          onChange={(e) => {
            if (e.target.value.length <= 1000) setDraft(e.target.value);
          }}
          placeholder={disabled ? (disabledPlaceholder || "This conversation is closed.") : "Type your message…"}
          rows={2}
          disabled={disabled}
          className="resize-none text-sm"
          data-testid="input-conversation-message"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          onClick={handleSend}
          disabled={sendMutation.isPending || !draft.trim() || disabled}
          data-testid="button-send-conversation"
        >
          {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
